import React, { useState, useMemo, useEffect } from 'react';
import Layout from 'Layout';
import { useTranslation } from 'react-i18next';
import countries from 'components/Country/countries.json';
import './style.scss';
import { Trophy } from 'lucide-react';
import { fetchLeaderboard } from './api';
import { Helmet } from 'react-helmet-async';

export interface LeaderboardEntry {
  rank: number;
  name: string;
  country: string;
  countryCode: string;
  profit: string;
  equity: string;
  achievedPercent: string;
  accountSize: string;
  accountSizeValue: number;
}


const Leaderboard: React.FC = () => {
  const { t } = useTranslation();
  const [selectedFilter, setSelectedFilter] = useState<string>('all');
  const [data, setData] = useState<LeaderboardEntry[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    setLoading(true);
    const loadLeaderboard = async () => {
      try {
        const dataObj = await fetchLeaderboard();
        setData(dataObj);
      } catch (error) {
        console.error('Failed to fetch leaderboard', error);
      }
      finally {
        setLoading(false);
      }
    };
    loadLeaderboard();
  }, []);

  useEffect(() => {
    setLoading(true);
    const applyFilter = async () => {
      try {
        const dataObj = await fetchLeaderboard(
          selectedFilter === 'all' ? undefined : parseInt(selectedFilter.replace('K', '000'))
        );
        setData(dataObj);
      } catch (error) {
        console.error('Failed to fetch leaderboard with filter', error);
      }
      finally {
        setLoading(false);
      }
    };
    applyFilter();
  }, [selectedFilter]);


  const topThree = data.slice(0, 3);
  const remainingEntries = data.slice(3, 100);

  const getCountryFlag = (countryCode: string): string => {
    const country = countries.find((c: any) => c.iso === countryCode);
    return country?.flag || '🏳️';
  };

  const getTrophyImage = (rank: number): string => {
    if (rank === 1) return '/images/leaderboard/gold.png';
    if (rank === 2) return '/images/leaderboard/silver.png';
    if (rank === 3) return '/images/leaderboard/bronze.png';
    return '';
  };

  const filters = ['all', '10K', '25K', '50K', '100K', '200K'];
  const filterLabels: { [key: string]: string } = {
    all: t('all'),
    '10K': '10K',
    '25K': '25K',
    '50K': '50K',
    '100K': '100K',
    '200K': '200K',
  };

  const currentDate = new Date();
  const formattedDate = `${currentDate.getDate()}/${currentDate.getMonth() + 1}/${currentDate.getFullYear()}`;

  const accountSizeGradient =
    'linear-gradient(90deg, rgba(233, 180, 96, 0.1019607843), rgba(233, 180, 96, 0))';

  return (
    <Layout>
      <Helmet>
        <title>Leaderboard - Lumir 13W Challenge</title>
      </Helmet>
      <div className="leaderboard-page min-h-screen">
        {/* Section 1: header + filters */}
        <div className="section-card mb-6">
          <div className="flex flex-col items-start gap-2 mb-4">
            <div className='flex flex-row gap-2 items-center border-b border-b-gray-200 pb-2 w-full'>
              <Trophy className='trophy-icon w-8 h-8' />
              <p className="leaderboard-title text-2xl font-bold mb-0">{t('Leaderboard')}</p>
            </div>
            <p className="leaderboard-subtitle text-base pt-2 pb-3 w-full border-b border-b-gray-200">
              {t('leaderboard_1sectionDecs')}
            </p>
          </div>

          <div className="flex gap-2 flex-wrap">
            {filters.map((filter) => (
              <button
                key={filter}
                className={`filter-button px-4 py-2 rounded-md text-sm font-medium transition-all ${selectedFilter === filter ? 'active' : ''
                  }`}
                onClick={() => setSelectedFilter(filter)}
              >
                {filterLabels[filter]}
              </button>
            ))}
          </div>
        </div>

        {/* Date indicator between sections */}
        <div className="flex items-center gap-2 mb-6 text-sm">
          <span className="w-2 h-2 rounded-full bg-green-500"></span>
          <span className="date-text text-lg">{formattedDate}</span>
        </div>

        {
          (data.length === 0 && !loading) ? (
            <main className="flex-grow flex flex-col items-center justify-center py-20 px-4">
              <section className="flex flex-col md:flex-row items-center md:items-start gap-8 max-w-4xl">
                {/* SVG Illustration */}
                <div className="w-48 h-48 md:w-64 md:h-64">
                  <svg
                    width="100%"
                    height="100%"
                    viewBox="0 0 200 200"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <circle
                      cx="100"
                      cy="100"
                      r="100"
                      fill="url(#paint0_radial_1466_4269)"
                      opacity="0.1"
                    />
                    <defs>
                      <radialGradient
                        id="paint0_radial_1466_4269"
                        cx="0"
                        cy="0"
                        r="1"
                        gradientUnits="userSpaceOnUse"
                        gradientTransform="translate(100, 100) rotate(90) scale(100)"
                      >
                        <stop stopColor="#60A5FA" />
                        <stop offset="1" stopColor="#A78BFA" stopOpacity="0" />
                      </radialGradient>
                    </defs>
                  </svg>
                </div>

                {/* Text Content */}
                <div className="text-center md:text-left">
                  <h2 className="text-2xl font-bold mb-4">{t('leaderboard_noData')}</h2>
                  <p className="text-gray-600">
                    {t('leaderboard_noDataDesc')}
                  </p>
                </div>
              </section>
            </main>
          ) : (
            data.length === 0 && loading ? (
              <div className="flex flex-col items-center justify-center py-20">
                <svg
                  className="animate-spin mb-4 h-10 w-10 text-gray-500"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                <p className="text-gray-500">{t('leaderboard_loading')}</p>
              </div>
            )
              :
              (
                <>
                  {/* Section 2: Top 3 cards */}
                  < div className="mb-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {topThree.map((entry) => (
                        <div
                          key={entry.rank}
                          className="top-card rounded-lg p-6 shadow-md hover:shadow-lg transition-all hover:-translate-y-1"
                        >
                          <div className="flex flex-row items-start justify-between">
                            <div className='flex flex-row items-center w-full'>
                              <div className="h-20 flex items-center justify-center mb-2">
                                <img
                                  src={getTrophyImage(entry.rank)}
                                  alt={`${entry.rank === 1 ? 'Gold' : entry.rank === 2 ? 'Silver' : 'Bronze'} trophy`}
                                  className="max-h-12 w-auto object-contain"
                                  onError={(e) => {
                                    (e.target as HTMLImageElement).style.display = 'none';
                                  }}
                                />
                              </div>
                              <div className='flex flex-col ml-2 items-start'>
                                <div className="top-card-name text-lg font-bold">{entry.name}</div>
                                <div className="flex items-center gap-2 text-sm">
                                  <span className="text-xl">{getCountryFlag(entry.countryCode)}</span>
                                  <span className="top-card-country-code font-medium">{entry.countryCode}</span>
                                </div>
                              </div>
                            </div>
                            <div className="top-card-rank text-md p-2 rounded-md font-semibold">{entry.rank}.</div>
                          </div>

                          <div className="top-card-metrics flex justify-between items-center mb-4 text-sm">
                            <div className="flex flex-col gap-1">
                              <span className="metric-label">{t('leaderboard_table_profit')}</span>
                              <span className="metric-value font-semibold">{entry.profit}</span>
                            </div>
                            <div className="flex flex-col gap-1">
                              <span className="metric-label">{t('leaderboard_table_equity')}</span>
                              <span className="metric-value font-semibold">{entry.equity}</span>
                            </div>
                            <div className="flex flex-col gap-1">
                              <span className="metric-label">{t('leaderboard_table_achievedPercent')}</span>
                              <span className="metric-value-achieved font-semibold">{entry.achievedPercent}</span>
                            </div>
                          </div>

                          <div
                            className="rounded-lg flex flex-row justify-between items-center px-2 py-3 text-center"
                            style={{ background: accountSizeGradient }}
                          >
                            <div className="text-sm font-semibold">
                              {t('leaderboard_table_accountSize')}
                            </div>
                            <div className="text-sm font-bold">
                              {entry.accountSize}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Section 3: Top 4-10 */}
                  <div className="leaderboard-table-container section-card rounded-xl p-6 shadow-md overflow-x-auto">
                    <table className="leaderboard-table w-full border-collapse">
                      <thead>
                        <tr>
                          <th className="px-4 py-3 text-left text-sm font-semibold">#</th>
                          <th className="px-4 py-3 text-left text-sm font-semibold">{t('leaderboard_table_traderName')}</th>
                          <th className="px-4 py-3 text-left text-sm font-semibold">{t('leaderboard_table_profit')}</th>
                          <th className="px-4 py-3 text-left text-sm font-semibold">{t('leaderboard_table_equity')}</th>
                          <th className="px-4 py-3 text-left text-sm font-semibold">{t('leaderboard_table_achievedPercent')}</th>
                          <th className="px-4 py-3 text-left text-sm font-semibold">{t('leaderboard_table_accountSize')}</th>
                          <th className="px-4 py-3 text-left text-sm font-semibold">{t('leaderboard_table_countryCode')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {remainingEntries.map((entry) => (
                          <tr
                            key={entry.rank}
                            className="table-row"
                          >
                            <td className="px-4 py-3 text-sm table-cell">{entry.rank}.</td>
                            <td className="px-4 py-3 text-sm table-cell">{entry.name}</td>
                            <td className="px-4 py-3 text-sm table-cell">{entry.profit}</td>
                            <td className="px-4 py-3 text-sm table-cell">{entry.equity}</td>
                            <td className="px-4 py-3 text-sm font-semibold !text-green-500 table-cell">{entry.achievedPercent}</td>
                            <td className="px-4 py-3 text-sm table-cell">{entry.accountSize}</td>
                            <td className="px-4 py-3 text-sm table-cell">
                              <div className="flex items-center gap-2">
                                <span className="text-lg">{getCountryFlag(entry.countryCode)}</span>
                                <span className="table-country-code font-medium">{entry.countryCode}</span>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )

          )
        }
      </div >
    </Layout >
  );
};

export default Leaderboard;

